/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { all, task } from 'ember-concurrency';
import ApiService from 'cio/services/api';
import {
  Suppression,
  DEFAULT_PAGE,
  DEFAULT_CATEGORY,
  DEFAULT_SEARCH,
  PER_PAGE,
  CATEGORIES_MAP,
  CATEGORIES,
} from 'cio/utils/email-suppression-list';

interface ListArgs {
  email?: string;
  category?: string;
  page?: number;
}

export default class List extends Component<ListArgs> {
  @service api!: ApiService;

  @tracked email: string = this.args.email ?? DEFAULT_SEARCH;
  @tracked category: string = this.args.category ?? DEFAULT_CATEGORY;
  @tracked page: number = this.args.page ?? DEFAULT_PAGE;

  @tracked changedEmail = '';
  @tracked changedPage = this.page;

  @tracked selectedEmails: string[] = [];
  @tracked suppressionsByCategory: Record<string, Suppression[]> = {};

  totalRecs = -1;
  perPage = PER_PAGE;
  categories = CATEGORIES_MAP;

  get suppressions() {
    return this.suppressionsByCategory[this.category];
  }

  get hasSuppressionsForAllCategories() {
    return CATEGORIES.every((category) => {
      return Object.keys(this.suppressionsByCategory).includes(category);
    });
  }

  fetchSuppressionsForAllCategoriesTask = task(
    {
      drop: true,
    },
    async () => {
      await all(CATEGORIES.map((category) => this.fetchSuppressionsTask.perform(category)));
      this.setActiveCategory();
    },
  );

  fetchSuppressionsTask = task(
    {
      enqueue: true,
    },
    async (category) => {
      let offset = (this.page - 1) * PER_PAGE;
      let results = await this.api.request(
        `suppression/${category}?email=${encodeURIComponent(this.email)}&offset=${offset}`,
      );

      this.suppressionsByCategory = {
        ...this.suppressionsByCategory,
        [category]: results.category.suppressions,
      };
    },
  );

  unsuppressEmailsTask = task(
    {
      drop: true,
    },
    async () => {
      let { category } = this;

      await this.api.post('delete_suppression', {
        data: {
          category,
          emails: this.selectedEmails,
        },
      });

      this.selectedEmails = [];
      this.fetchSuppressions();
    },
  );

  @action
  updateAttrs() {
    /// If we're searching and the category changes, but the email or page
    // don't, we don't want to reload everything because we already have the
    // search results for each category.
    if (this.email && !this.emailChanged() && !this.pageChanged()) {
      return;
    }

    this.fetchSuppressions();

    this.changedEmail = this.email;
    this.changedPage = this.page;
  }

  fetchSuppressions() {
    this.suppressionsByCategory = {};

    // If we search (or change the search query), we fetch suppressions for
    // each of the categories, otherwise we just go with the current category,
    // whichever that is.
    if (this.email && this.emailChanged()) {
      this.fetchSuppressionsForAllCategoriesTask.perform();
    } else {
      this.fetchSuppressionsTask.perform(this.category);
    }
  }

  emailChanged() {
    return this.email !== this.changedEmail;
  }

  pageChanged() {
    return this.page !== this.changedPage;
  }

  setActiveCategory() {
    let firstCategoryWithSuppressions = Object.keys(this.suppressionsByCategory).find((key) => {
      return this.suppressionsByCategory[key].length > 0;
    });

    if (firstCategoryWithSuppressions == null) {
      return;
    }

    this.category = firstCategoryWithSuppressions;
  }

  @action
  onSearch(email: string) {
    this.email = email;
    this.updateAttrs();
  }

  @action
  onCategoryChange(category: string) {
    this.category = category;
    this.page = 1;
    this.updateAttrs();
  }

  @action
  onPageChange(page: number) {
    this.page = page;
    this.updateAttrs();
  }

  @action
  onSelect(suppression: Suppression) {
    let { email } = suppression;

    if (this.selectedEmails.includes(email)) {
      this.selectedEmails = [...this.selectedEmails.filter((selectedEmail) => selectedEmail !== email)];
    } else {
      this.selectedEmails = [...this.selectedEmails, email];
    }
  }
}
